import axios from 'axios'
import { ConsentPermissionDescriptor, UserPiiPermissions } from '@/types'

/**
 * OAuth 2.0 embedded-SSO flow API (Authorization Code + PKCE).
 *
 * Base path `/oauth`, version segment `v1.0`. All responses use the envelope
 * `{ status, results: [...], errors: [] }`.
 *
 * The backend sets an HttpOnly `__Host-dupr_oauth` browser-binding cookie during
 * launch/authorize. It is NEVER read in JS, but the cookie-bound calls below MUST
 * send credentials (`withCredentials: true`) so the browser attaches it.
 *
 * Credentials (email/password, bearer tokens) are only ever posted to DUPR — never
 * to a partner. This module deliberately does NOT use `postMessage`.
 */

const OAUTH_BASE = '/oauth/v1.0'
const LOGIN_TOKEN_PATH = `${OAUTH_BASE}/login-token`
const FLOW_PATH = `${OAUTH_BASE}/flow`
const AUTHENTICATE_PATH = `${OAUTH_BASE}/authorize/authenticate`
const CALLBACK_PATH = `${OAUTH_BASE}/authorize/callback`

/** Standard DUPR response envelope for the OAuth endpoints. */
interface OAuthEnvelope<T> {
  status: 'SUCCESS' | 'FAILURE'
  results: T[]
  errors?: { message?: string }[]
}

/** Result of minting a one-time OAuth login token (OTC). */
export interface OAuthLoginToken {
  token: string
  expiresIn: number
}

/** Context returned by GET /oauth/v1.0/flow for the consent SPA. */
export interface OAuthFlowContext {
  partnerName: string
  authenticated: boolean
  connectingUserDuprId: string | null
  permissions: ConsentPermissionDescriptor[]
  existingPermissions: UserPiiPermissions | null
  /** Which of `permissions` the member already granted, by key. Absent on older servers. */
  existingGrants?: Record<string, boolean> | null
  /** Required keys the member has not granted — why they are being asked again. */
  missingRequired?: string[]
}

/** Result of POST /oauth/v1.0/authorize/callback. Always a top-level redirect target. */
export interface OAuthCallbackResult {
  redirectUrl: string
}

const unwrap = <T>(envelope: OAuthEnvelope<T>): T => {
  if (envelope.status !== 'SUCCESS' || !envelope.results?.[0]) {
    throw new Error(envelope.errors?.[0]?.message || 'The connection request could not be completed. Please try again.')
  }
  return envelope.results[0]
}

/**
 * Mint a one-time OAuth login token (OTC) from the authenticated DUPR session.
 * Cookie-authenticated: the HttpOnly first-party session cookie is sent via `withCredentials`
 * (the SPA never holds a bearer). The OTC is single-use and is consumed by a top-level navigation
 * to `/oauth/v1.0/launch`.
 */
export const mintOAuthLoginToken = async (): Promise<OAuthLoginToken> => {
  const response = await axios
    .create()
    .post<OAuthEnvelope<OAuthLoginToken>>(LOGIN_TOKEN_PATH, {}, { withCredentials: true, timeout: 30000 })
  return unwrap(response.data)
}

/**
 * Fetch the consent context for a pending flow. Cookie-bound: the `__Host-dupr_oauth`
 * browser-binding cookie is sent via `withCredentials`.
 */
export const getOAuthFlow = async (flowId: string): Promise<OAuthFlowContext> => {
  const response = await axios
    .create()
    .get<
      OAuthEnvelope<OAuthFlowContext>
    >(FLOW_PATH, { params: { flow: flowId }, withCredentials: true, timeout: 30000 })
  return unwrap(response.data)
}

/**
 * Bind the logged-in DUPR user to a pending flow. Cookie-authenticated: the HttpOnly first-party
 * session identifies the user and the `__Host-dupr_oauth` browser-binding cookie authorizes the
 * flow, both sent via `withCredentials`. After a successful call, re-fetch the flow — it will now
 * report `authenticated: true`.
 */
export const authenticateOAuthFlow = async (flowId: string): Promise<void> => {
  await axios
    .create()
    .post<OAuthEnvelope<unknown>>(AUTHENTICATE_PATH, { flowId }, { withCredentials: true, timeout: 30000 })
}

/**
 * Submit the user's consent decision. Cookie-bound (the `__Host-dupr_oauth` nonce is
 * re-verified server-side). The caller MUST `window.location.assign(redirectUrl)` on the
 * result — for BOTH approve and deny — so the code (approve) or error (deny) is delivered
 * to the partner via a top-level redirect. The browser never receives a token.
 */
export const submitOAuthConsent = async (
  flowId: string,
  approve: boolean,
  permissions?: UserPiiPermissions,
  /** The key-based answer, one entry per descriptor shown; wins over `permissions` on the server. */
  grants?: Record<string, boolean>
): Promise<OAuthCallbackResult> => {
  const response = await axios
    .create()
    .post<
      OAuthEnvelope<OAuthCallbackResult>
    >(CALLBACK_PATH, { flowId, approve, ...(permissions ? { permissions } : {}), ...(grants ? { grants } : {}) }, { withCredentials: true, timeout: 30000 })
  return unwrap(response.data)
}

/**
 * Build the top-level `/oauth/v1.0/launch` URL that consumes an OTC and bounces the browser
 * through the partner login. Optional promo context is forwarded to the backend launch hop.
 */
export const buildOAuthLaunchUrl = (otc: string, clientId: string, extraParams?: Record<string, string>): string => {
  const params = new URLSearchParams({ otc, client_id: clientId })
  if (extraParams) {
    Object.entries(extraParams).forEach(([key, value]) => {
      if (value) params.set(key, value)
    })
  }
  return `${OAUTH_BASE}/launch?${params.toString()}`
}
